import type { NotesPresentation } from '@/lib/matrix-notes-prefs-store';
import { NOTE_LEVEL_LABELS } from '@/lib/note-format';
import { cn } from '@/lib/utils';
import {
  NOTE_VISIBILITIES,
  type NoteVisibility,
  type UserRole,
  canUseNoteVisibility,
} from '@mss_creator/shared';
import { Icon } from '@pms-digital/design-system';
import { NotesMultiFilter } from './notes-multi-filter';
import { NotesSearchInput } from './notes-search-input';
import { NotesSortControls } from './notes-sort-controls';
import {
  DEFAULT_NOTES_SORT_DIRECTIONS,
  NOTES_STATUSES,
  NOTES_STATUS_LABELS,
  NOTES_TARGET_TYPES,
  NOTES_TARGET_TYPE_LABELS,
  type NotesViewSettings,
} from './notes-view-search';

export interface NotesViewFiltersProps {
  settings: NotesViewSettings;
  /** The authors of the matrix's discussions: the options of « Auteur ». */
  authors: { id: string; name: string }[];
  /** The role in force: a client role is not offered « Équipe PMS ». */
  role: UserRole;
  onChange: (patch: Partial<NotesViewSettings>) => void;
  /** Cards or table: remembered by the page besides being in the URL. */
  onPresentationChange: (presentation: NotesPresentation) => void;
}

const SEGMENT_CLASSES =
  'h-9 px-3 text-small focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-bleu-nuit';

/**
 * The controls of the Notes view (planche 12), in one row: text search, the
 * status segmented control, the three list filters, then « Trier par » with its
 * inversion and the cards / table switch at the far right.
 */
export function NotesViewFilters({
  settings,
  authors,
  role,
  onChange,
  onPresentationChange,
}: NotesViewFiltersProps) {
  const levels: NoteVisibility[] = NOTE_VISIBILITIES.filter((level) =>
    canUseNoteVisibility(role, level),
  );

  return (
    <div className="flex flex-wrap items-center gap-2">
      <NotesSearchInput value={settings.q} onChange={(q) => onChange({ q })} />

      <fieldset
        aria-label="Statut des discussions"
        className="inline-flex overflow-hidden rounded-md border-2 border-gray-200"
      >
        {NOTES_STATUSES.map((status) => (
          <button
            key={status}
            type="button"
            aria-pressed={settings.status === status}
            onClick={() => onChange({ status })}
            className={cn(
              SEGMENT_CLASSES,
              settings.status === status
                ? 'bg-bleu-nuit font-semibold text-white'
                : 'bg-white text-noir hover:bg-gray-50',
            )}
          >
            {NOTES_STATUS_LABELS[status]}
          </button>
        ))}
      </fieldset>

      <NotesMultiFilter
        label="Niveau"
        options={levels.map((level) => ({ value: level, label: NOTE_LEVEL_LABELS[level] }))}
        selected={settings.levels}
        onChange={(selected) => onChange({ levels: selected })}
      />
      <NotesMultiFilter
        label="Auteur"
        options={authors.map((author) => ({ value: author.id, label: author.name }))}
        selected={settings.authors}
        onChange={(selected) => onChange({ authors: selected })}
      />
      <NotesMultiFilter
        label="Cible"
        options={NOTES_TARGET_TYPES.map((type) => ({
          value: type,
          label: NOTES_TARGET_TYPE_LABELS[type],
        }))}
        selected={settings.targetTypes}
        onChange={(selected) => onChange({ targetTypes: selected })}
      />

      <div className="ml-auto flex items-center gap-2">
        <NotesSortControls
          sort={settings.sort}
          dir={settings.dir}
          onSortChange={(sort) => onChange({ sort, dir: DEFAULT_NOTES_SORT_DIRECTIONS[sort] })}
          onToggleDirection={() => onChange({ dir: settings.dir === 'asc' ? 'desc' : 'asc' })}
        />
        <fieldset
          aria-label="Présentation"
          className="inline-flex overflow-hidden rounded-md border-2 border-gray-200"
        >
          <button
            type="button"
            aria-pressed={settings.presentation === 'cartes'}
            aria-label="Cartes"
            title="Cartes"
            onClick={() => onPresentationChange('cartes')}
            className={cn(
              SEGMENT_CLASSES,
              'px-2',
              settings.presentation === 'cartes'
                ? 'bg-bleu-nuit text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50',
            )}
          >
            <Icon name="grid_view" size={16} />
          </button>
          <button
            type="button"
            aria-pressed={settings.presentation === 'tableau'}
            aria-label="Tableau"
            title="Tableau"
            onClick={() => onPresentationChange('tableau')}
            className={cn(
              SEGMENT_CLASSES,
              'px-2',
              settings.presentation === 'tableau'
                ? 'bg-bleu-nuit text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50',
            )}
          >
            <Icon name="list" size={16} />
          </button>
        </fieldset>
      </div>
    </div>
  );
}
